import { chakra } from '@enechain/ecloud-designsystem'
import type { HTMLChakraProps } from '@enechain/ecloud-designsystem'

type GripDotsVerticalIconProps = Omit<HTMLChakraProps<'svg'>, 'children'> & {
  /** DS icon と同じ使い勝手にするための寸法 prop。 */
  boxSize?: string
}

/**
 * 掴んで並べ替える drag handle の grip (縦 2 列 × 3 段のドット)。
 *
 * ## [DS gap] DS に追加されるまでの暫定実装
 *
 * Font Awesome の `grip-dots-vertical` (classic / solid) と同じ絵
 * (https://fontawesome.com/icons/classic/solid/grip-dots-vertical)。
 * **ecloud-icons 0.4.0 には無い** — `Grip` を含む export は
 * `FaGripLinesRegular` (横 2 本線) だけで、 ドットの grip は存在しない。
 *
 * 2026-08-24 デザイナー判断: **DS に今後追加する前提で、 この絵を使う**。
 * 「最も近い DS icon に寄せる」 規約の例外で、
 * 横線の grip では 8080 と絵が変わってしまうため。
 *
 * **DS に追加されたら、 この component を消して DS の icon に差し替えること。**
 *
 * ## [実装メモ] DS `Icon` ではなく `chakra.svg` で組む
 *
 * DS `Icon` に**複数の子**を渡すと svg でラップされず、 最初の子 (`<circle>`) が
 * そのまま root 要素になって**何も描画されない** (2026-08-24 実測: root の tagName が
 * `CIRCLE` になり、 中に circle が 0 個)。 DS icon はどれも子が `<path>` 1 個なので
 * この経路を踏まない。 6 点ドットは子が 6 個要るため、 svg を自分で持つ。
 *
 * ## path の出所
 *
 * design 8080 の `admin/case-templates.html` が持つ `_ICO_GRIP` の svg をそのまま写す
 * (= プロトで実際に描かれている絵と 1px も違わないようにするため。 Font Awesome の
 * 原寸 viewBox `0 0 256 512` から起こし直すと大きさ・余白が変わる)。
 */
export const GripDotsVerticalIcon = ({
  boxSize = '1em',
  ...rest
}: GripDotsVerticalIconProps): React.ReactElement => (
  <chakra.svg
    width={boxSize}
    height={boxSize}
    fill="currentColor"
    focusable="false"
    viewBox="0 0 24 24"
    {...rest}
  >
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </chakra.svg>
)
